import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import * as Dialog from "@radix-ui/react-dialog";
import * as Select from "@radix-ui/react-select";
import {
  DndContext,
  useDraggable,
  useDroppable,
  closestCenter,
  useSensors,
  useSensor,
  PointerSensor,
  MeasuringStrategy,
} from "@dnd-kit/core";
import type {
  DragStartEvent,
  DragEndEvent,
  DragMoveEvent,
} from "@dnd-kit/core";
import {
  addDays,
  addMonths,
  addWeeks,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  startOfMonth,
  startOfToday,
  startOfWeek,
  subMonths,
  subWeeks,
  subDays,
  parse,
} from "date-fns";
import {
  TbChevronLeft,
  TbChevronRight,
  TbCalendar,
  TbFilter,
  TbX,
  TbSearch,
  TbChevronDown,
  TbCheck,
} from "react-icons/tb";
import type { Task } from "@/types/task";
import { useAllUsers } from "@/hooks/useUsers";
import { useTeams } from "@/hooks/useTeams";
import { useTaskContext } from "@/context/taskContext";

// Helper: clamp string array uniqueness
const uniq = (arr: string[]) => Array.from(new Set(arr.filter(Boolean)));

// Transform task type for calendar view
interface CalendarTask {
  id: string;
  title: string;
  dueAt: Date;
  assignee: string;
  taskType: string;
  assetType: string;
  status: string;
  location: string;
}

interface TaskCalendarProps {
  tasks: Task[];
  onTaskClick?: (task: Task) => void;
}

type ClientPoint = {
  x: number;
  y: number;
};

const getEventClientPoint = (
  event: Event | null | undefined
): ClientPoint | null => {
  if (!event) return null;

  const candidate = event as Event & {
    clientX?: number;
    clientY?: number;
    touches?: TouchList;
    changedTouches?: TouchList;
  };

  if (
    typeof candidate.clientX === "number" &&
    typeof candidate.clientY === "number"
  ) {
    return { x: candidate.clientX, y: candidate.clientY };
  }

  const touch = candidate.touches?.[0] ?? candidate.changedTouches?.[0];
  if (touch) {
    return { x: touch.clientX, y: touch.clientY };
  }

  return null;
};

export const TaskCalendar: React.FC<TaskCalendarProps> = ({
  tasks,
  onTaskClick,
}) => {
  const [view, setView] = useState<"day" | "week" | "month">("month");
  const [cursor, setCursor] = useState(startOfToday());
  const [draggedTask, setDraggedTask] = useState<CalendarTask | null>(null);
  const [pointerPosition, setPointerPosition] = useState<ClientPoint | null>(
    null
  );
  const dragStartPointerRef = useRef<ClientPoint | null>(null);
  const pointerMoveHandlerRef = useRef<((event: Event) => void) | null>(null);
  const pointerTrackingRafRef = useRef<number | null>(null);
  const isPointerTrackingActiveRef = useRef(false);

  const updatePointerFromEvent = useCallback((event: Event) => {
    const point = getEventClientPoint(event);
    if (!point) return;

    if (pointerTrackingRafRef.current !== null) {
      cancelAnimationFrame(pointerTrackingRafRef.current);
    }

    pointerTrackingRafRef.current = requestAnimationFrame(() => {
      setPointerPosition(point);
    });
  }, []);

  const startPointerTracking = useCallback(() => {
    if (isPointerTrackingActiveRef.current) return;

    const listener = (event: Event) => {
      updatePointerFromEvent(event);
    };

    pointerMoveHandlerRef.current = listener;
    window.addEventListener("pointermove", listener);
    window.addEventListener("touchmove", listener, { passive: false });
    isPointerTrackingActiveRef.current = true;
  }, [updatePointerFromEvent]);

  const stopPointerTracking = useCallback(() => {
    if (!isPointerTrackingActiveRef.current) return;

    const listener = pointerMoveHandlerRef.current;
    if (listener) {
      window.removeEventListener("pointermove", listener);
      window.removeEventListener("touchmove", listener);
      pointerMoveHandlerRef.current = null;
    }

    if (pointerTrackingRafRef.current !== null) {
      cancelAnimationFrame(pointerTrackingRafRef.current);
      pointerTrackingRafRef.current = null;
    }

    isPointerTrackingActiveRef.current = false;
    dragStartPointerRef.current = null;
    setPointerPosition(null);
  }, []);

  useEffect(() => {
    return () => {
      if (pointerMoveHandlerRef.current) {
        window.removeEventListener(
          "pointermove",
          pointerMoveHandlerRef.current
        );
        window.removeEventListener("touchmove", pointerMoveHandlerRef.current);
        pointerMoveHandlerRef.current = null;
      }
      if (pointerTrackingRafRef.current !== null) {
        cancelAnimationFrame(pointerTrackingRafRef.current);
        pointerTrackingRafRef.current = null;
      }
      isPointerTrackingActiveRef.current = false;
    };
  }, []);

  // Modal state for "view more" functionality
  const [viewMoreModal, setViewMoreModal] = useState<{
    isOpen: boolean;
    date: Date | null;
    tasks: CalendarTask[];
  }>({ isOpen: false, date: null, tasks: [] });

  // Local optimistic state for immediate UI updates
  const [optimisticUpdates, setOptimisticUpdates] = useState<
    Record<string, Partial<Task>>
  >({});

  // Clean up optimistic updates when the actual task data changes to match
  useEffect(() => {
    const updatedTaskIds = Object.keys(optimisticUpdates);
    const toCleanup: string[] = [];

    updatedTaskIds.forEach((taskId) => {
      const optimisticUpdate = optimisticUpdates[taskId];
      const actualTask = tasks.find((t) => t.id === taskId);

      if (actualTask && optimisticUpdate) {
        // Check if the actual task now matches our optimistic update
        const isMatching = Object.keys(optimisticUpdate).every(
          (key) =>
            actualTask[key as keyof Task] ===
            optimisticUpdate[key as keyof Task]
        );

        if (isMatching) {
          toCleanup.push(taskId);
        }
      }
    });

    if (toCleanup.length > 0) {
      setOptimisticUpdates((prev) => {
        const cleaned = { ...prev };
        toCleanup.forEach((id) => delete cleaned[id]);
        return cleaned;
      });
    }
  }, [tasks, optimisticUpdates]);

  // Date field selection - which field to use for plotting tasks on calendar
  const [dateField, setDateField] = useState<"due_date" | "start_date">(
    "due_date"
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 4,
      },
    })
  );

  // Get task context for updating tasks
  const { updateTask } = useTaskContext();

  // Apply optimistic updates to tasks for immediate UI feedback
  const optimisticTasks = useMemo(() => {
    return tasks.map((task) => {
      const update = optimisticUpdates[task.id];
      return update ? { ...task, ...update } : task;
    });
  }, [tasks, optimisticUpdates]);

  // Filters
  const [assignees, setAssignees] = useState<string[]>([]);
  const [taskTypes, setTaskTypes] = useState<string[]>([]);
  const [assetTypes, setAssetTypes] = useState<string[]>([]);
  const [searchText, setSearchText] = useState("");

  // Get users and teams data
  const { data: users = [] } = useAllUsers();
  const { data: teams = [] } = useTeams();

  // Helper function to get assignee display name
  const getAssigneeDisplayName = useCallback(
    (task: Task) => {
      if (task.assignee_type === "User") {
        const user = users.find((u) => u.id === task.assignee);
        if (user) {
          return user.first_name || user.last_name
            ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
            : user.email;
        }
      } else if (task.assignee_type === "Team") {
        const team = teams.find((t) => t.id === task.assignee);
        if (team) {
          return team.name;
        }
      }
      return task.assignee_type; // fallback to assignee type
    },
    [users, teams]
  );

  // Helper function to get the date for plotting based on selected field
  const getTaskDate = useCallback(
    (task: Task): Date => {
      let dateString: string | undefined;

      switch (dateField) {
        case "due_date":
          dateString = task.due_date;
          break;
        case "start_date":
          dateString = task.start_date;
          break;
        default:
          dateString = task.due_date;
      }

      if (!dateString) return new Date();

      // Handle both plain dates (yyyy-MM-dd) and ISO strings robustly.
      // Always extract just the date portion and parse as local date to avoid
      // timezone shifting (e.g., 2024-01-15T00:00:00.000Z becoming Jan 14 in PST)
      const isPlainDate = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
      const datePortion = isPlainDate
        ? dateString
        : dateString.substring(0, 10);
      const parsed = parse(datePortion, "yyyy-MM-dd", new Date());

      // Fallback if parsing failed
      return isNaN(parsed.getTime()) ? new Date(dateString) : parsed;
    },
    [dateField]
  );

  // Transform tasks to match the calendar component's expected format
  const transformedTasks = useMemo(
    (): CalendarTask[] =>
      optimisticTasks
        .filter((task) => {
          // Only include tasks that have the selected date field populated
          switch (dateField) {
            case "due_date":
              return !!task.due_date;
            case "start_date":
              return !!task.start_date;
            default:
              return !!task.due_date;
          }
        })
        .map((task) => ({
          id: task.id,
          title: task.title,
          dueAt: getTaskDate(task),
          assignee: getAssigneeDisplayName(task),
          taskType: task.category?.label || "General",
          assetType: task.priority?.label || "Normal",
          status: task.status?.label || "Open",
          location: task.asset_field || "",
        })),
    [optimisticTasks, getAssigneeDisplayName, dateField, getTaskDate]
  );

  // Handle task click
  const handleTaskClick = useCallback(
    (calendarTask: CalendarTask) => {
      if (onTaskClick) {
        // Find the original task by ID
        const originalTask = tasks.find((task) => task.id === calendarTask.id);
        if (originalTask) {
          onTaskClick(originalTask);
        }
      }
    },
    [onTaskClick, tasks]
  );

  // Derived lists for filter dropdowns
  const allAssignees = useMemo(
    () => uniq(transformedTasks.map((t) => t.assignee)),
    [transformedTasks]
  );
  const allTaskTypes = useMemo(
    () => uniq(transformedTasks.map((t) => t.taskType)),
    [transformedTasks]
  );
  const allAssetTypes = useMemo(
    () => uniq(transformedTasks.map((t) => t.assetType)),
    [transformedTasks]
  );

  // Apply filters
  const filtered = useMemo(() => {
    return transformedTasks.filter((t) => {
      if (assignees.length && !assignees.includes(t.assignee)) return false;
      if (taskTypes.length && !taskTypes.includes(t.taskType)) return false;
      if (assetTypes.length && !assetTypes.includes(t.assetType)) return false;
      if (
        searchText &&
        !`${t.title} ${t.assignee} ${t.taskType} ${t.assetType} ${
          t.location ?? ""
        }`
          .toLowerCase()
          .includes(searchText.toLowerCase())
      )
        return false;
      return true;
    });
  }, [transformedTasks, assignees, taskTypes, assetTypes, searchText]);

  // Drag and drop handlers
  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      let point = getEventClientPoint(event.activatorEvent);

      if (!point) {
        const rect =
          event.active.rect.current?.translated ||
          event.active.rect.current?.initial;
        if (rect) {
          point = {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
          };
        }
      }

      if (point) {
        dragStartPointerRef.current = point;
        setPointerPosition(point);
      } else {
        dragStartPointerRef.current = null;
        setPointerPosition(null);
      }

      // Find the task being dragged from the transformed tasks
      const task = transformedTasks.find((t) => t.id === event.active.id);
      if (task) {
        setDraggedTask(task);
        if (view !== "day") {
          startPointerTracking();
        }
        // Close the modal if it's open to reveal the calendar for dropping
        if (viewMoreModal.isOpen) {
          setViewMoreModal((prev) => ({ ...prev, isOpen: false }));
        }
      }
    },
    [startPointerTracking, transformedTasks, view, viewMoreModal.isOpen]
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      stopPointerTracking();

      if (!over || !draggedTask) {
        setDraggedTask(null);
        return;
      }

      // Extract the date from the droppable ID (format: "day-YYYY-MM-DD")
      const dateStr = over.id.toString().replace("day-", "");
      // Parse as a local date to avoid timezone shifting to the wrong day
      const targetDate = parse(dateStr, "yyyy-MM-dd", new Date());

      if (isNaN(targetDate.getTime())) {
        setDraggedTask(null);
        return;
      }

      // Find the original task
      const originalTask = tasks.find((t) => t.id === active.id);
      if (!originalTask) {
        setDraggedTask(null);
        return;
      }

      // Prepare the update data - preserve the duration between start and end dates
      const updates: Partial<Task> = {};
      const formattedTargetDate = format(targetDate, "yyyy-MM-dd");

      // Parse both dates from the original task
      const originalStartDate = originalTask.start_date
        ? parse(
            originalTask.start_date.substring(0, 10),
            "yyyy-MM-dd",
            new Date()
          )
        : null;
      const originalDueDate = originalTask.due_date
        ? parse(
            originalTask.due_date.substring(0, 10),
            "yyyy-MM-dd",
            new Date()
          )
        : null;

      if (dateField === "due_date") {
        updates.due_date = formattedTargetDate;
        // Calculate offset: how many calendar days did the due_date move?
        if (originalStartDate && originalDueDate) {
          const offsetDays = differenceInCalendarDays(
            targetDate,
            originalDueDate
          );
          const newStartDate = addDays(originalStartDate, offsetDays);
          updates.start_date = format(newStartDate, "yyyy-MM-dd");
        }
      } else if (dateField === "start_date") {
        updates.start_date = formattedTargetDate;
        // Calculate offset: how many calendar days did the start_date move?
        if (originalStartDate && originalDueDate) {
          const offsetDays = differenceInCalendarDays(
            targetDate,
            originalStartDate
          );
          const newDueDate = addDays(originalDueDate, offsetDays);
          updates.due_date = format(newDueDate, "yyyy-MM-dd");
        }
      }

      // IMMEDIATELY update local state for instant visual feedback
      setOptimisticUpdates((prev) => ({
        ...prev,
        [originalTask.id]: updates,
      }));

      // Clear drag state immediately
      setDraggedTask(null);

      // Update database in background
      try {
        await updateTask(originalTask.id, updates);
        // Note: optimistic update cleanup is handled by useEffect
      } catch (error) {
        console.error("Failed to update task date:", error);

        // Error: immediately remove the optimistic update to show the original data
        setOptimisticUpdates((prev) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [originalTask.id]: _, ...rest } = prev;
          return rest;
        });

        // TODO: Show error notification to user
      }
    },
    [draggedTask, dateField, stopPointerTracking, tasks, updateTask]
  );

  const handleDragMove = useCallback((event: DragMoveEvent) => {
    if (isPointerTrackingActiveRef.current) {
      return;
    }

    const point = getEventClientPoint(event.activatorEvent);
    if (point) {
      setPointerPosition(point);
      return;
    }

    if (dragStartPointerRef.current) {
      setPointerPosition({
        x: dragStartPointerRef.current.x + event.delta.x,
        y: dragStartPointerRef.current.y + event.delta.y,
      });
    }
  }, []);

  const handleDragCancel = useCallback(() => {
    stopPointerTracking();
    setDraggedTask(null);
  }, [stopPointerTracking]);

  // Handler for "view more" modal
  const handleViewMore = useCallback((date: Date, tasks: CalendarTask[]) => {
    setViewMoreModal({
      isOpen: true,
      date,
      tasks,
    });
  }, []);

  // Time window based on view
  const weekStart = startOfWeek(cursor, { weekStartsOn: 0 });
  const weekEnd = endOfWeek(cursor, { weekStartsOn: 0 });
  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);

  // Tasks for a day
  const tasksOn = (day: Date) =>
    filtered.filter((t) => isSameDay(t.dueAt, day));

  // Grid days for month view (show full weeks spanning the month)
  const monthGridDays = useMemo(() => {
    const start = startOfWeek(monthStart, { weekStartsOn: 0 });
    const end = endOfWeek(monthEnd, { weekStartsOn: 0 });
    return eachDayOfInterval({ start, end });
  }, [monthStart, monthEnd]);

  // Tasks for week interval
  const weekTasks = useMemo(
    () =>
      filtered.filter((t) =>
        isWithinInterval(t.dueAt, { start: weekStart, end: weekEnd })
      ),
    [filtered, weekStart, weekEnd]
  );

  // Day tasks (today = cursor)
  const dayTasks = useMemo(
    () => filtered.filter((t) => isSameDay(t.dueAt, cursor)),
    [filtered, cursor]
  );

  // Navigation handlers
  const goPrev = () => {
    if (view === "month") setCursor(subMonths(cursor, 1));
    else if (view === "week") setCursor(subWeeks(cursor, 1));
    else setCursor(subDays(cursor, 1));
  };
  const goNext = () => {
    if (view === "month") setCursor(addMonths(cursor, 1));
    else if (view === "week") setCursor(addWeeks(cursor, 1));
    else setCursor(addDays(cursor, 1));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragMove={handleDragMove}
      onDragCancel={handleDragCancel}
      // Fix for modal positioning - use measuring strategy for better positioning in modals
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.Always,
        },
      }}
    >
      <div className="w-full h-full p-4 md:p-6 relative">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <TbCalendar className="w-6 h-6 text-[#444444]" />
            <h1 className="text-xl font-semibold text-[#444444]">
              Task Calendar
            </h1>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={goPrev}
              className="cursor-pointer p-2 rounded-xl border border-gray-200 hover:bg-gray-50 transition"
            >
              <TbChevronLeft className="w-5 h-5 text-[#828282]" />
            </button>
            <div className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium min-w-40 text-center text-[#444444]">
              {view === "month" && format(cursor, "MMMM yyyy")}
              {view === "week" &&
                `${format(weekStart, "MMM d")} – ${format(
                  weekEnd,
                  "MMM d, yyyy"
                )}`}
              {view === "day" && format(cursor, "EEEE, MMM d, yyyy")}
            </div>
            <button
              onClick={goNext}
              className="cursor-pointer p-2 rounded-xl border border-gray-200 hover:bg-gray-50 transition"
            >
              <TbChevronRight className="w-5 h-5 text-[#828282]" />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <ViewToggle view={view} onChange={setView} />
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-3 mb-4">
          {/* Date Field Selection */}
          <div className="relative">
            <Select.Root
              value={dateField}
              onValueChange={(value) =>
                setDateField(value as "due_date" | "start_date")
              }
            >
              <Select.Trigger className="w-full flex items-center justify-between gap-2 border border-gray-200 rounded-xl px-3 py-1 bg-gray-100 text-sm text-[#444444] focus:outline-none cursor-pointer">
                <div className="text-left pt-4">
                  <div className="absolute top-1.5 left-3 text-[11px] uppercase tracking-wide text-[#828282]">
                    DATE FIELD
                  </div>
                  <Select.Value />
                </div>
                <Select.Icon asChild>
                  <TbChevronDown className="w-4 h-4 text-[#828282]" />
                </Select.Icon>
              </Select.Trigger>
              <Select.Portal>
                <Select.Content className="relative z-1200 max-h-96 min-w-32 overflow-hidden rounded-lg border border-gray-200 bg-white text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2">
                  <Select.ScrollUpButton className="flex cursor-default items-center justify-center py-1">
                    <TbChevronDown className="h-4 w-4 rotate-180" />
                  </Select.ScrollUpButton>
                  <Select.Viewport className="p-1">
                    <Select.Item
                      value="due_date"
                      className="relative flex w-full cursor-default select-none items-center rounded-md py-2 pl-8 pr-2 text-sm outline-none hover:bg-gray-100 focus:bg-gray-100 data-disabled:pointer-events-none data-disabled:opacity-50"
                    >
                      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                        <Select.ItemIndicator>
                          <TbCheck className="h-4 w-4" />
                        </Select.ItemIndicator>
                      </span>
                      <Select.ItemText>Due Date</Select.ItemText>
                    </Select.Item>
                    <Select.Item
                      value="start_date"
                      className="relative flex w-full cursor-default select-none items-center rounded-md py-2 pl-8 pr-2 text-sm outline-none hover:bg-gray-100 focus:bg-gray-100 data-disabled:pointer-events-none data-disabled:opacity-50"
                    >
                      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                        <Select.ItemIndicator>
                          <TbCheck className="h-4 w-4" />
                        </Select.ItemIndicator>
                      </span>
                      <Select.ItemText>Start Date</Select.ItemText>
                    </Select.Item>
                  </Select.Viewport>
                  <Select.ScrollDownButton className="flex cursor-default items-center justify-center py-1">
                    <TbChevronDown className="h-4 w-4" />
                  </Select.ScrollDownButton>
                </Select.Content>
              </Select.Portal>
            </Select.Root>
          </div>
          <MultiSelect
            label="Assignee"
            options={allAssignees}
            values={assignees}
            setValues={setAssignees}
          />
          <MultiSelect
            label="Task Type"
            options={allTaskTypes}
            values={taskTypes}
            setValues={setTaskTypes}
          />
          <MultiSelect
            label="Asset Type"
            options={allAssetTypes}
            values={assetTypes}
            setValues={setAssetTypes}
          />
          <div className="lg:col-span-2 flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-1 bg-gray-100">
            <TbSearch className="w-4 h-4 text-[#828282]" />
            <input
              className="w-full bg-transparent outline-none text-[#444444] text-sm placeholder:text-[#828282]"
              placeholder="Search tasks, locations…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
        </div>

        {/* Content */}
        <div className="rounded-2xl border border-gray-200 overflow-hidden bg-white">
          {view === "month" && (
            <MonthGrid
              days={monthGridDays}
              cursor={cursor}
              onSelect={(day) => {
                setCursor(day);
                setView("day");
              }}
              tasksOn={tasksOn}
              onTaskClick={handleTaskClick}
              onViewMore={handleViewMore}
            />
          )}
          {view === "week" && (
            <WeekView
              start={weekStart}
              end={weekEnd}
              tasks={weekTasks}
              onSelectDay={(day) => {
                setCursor(day);
                setView("day");
              }}
              onTaskClick={handleTaskClick}
            />
          )}
          {view === "day" && (
            <DayView
              day={cursor}
              tasks={dayTasks}
              onTaskClick={handleTaskClick}
            />
          )}
        </div>
      </div>
      {typeof document !== "undefined" &&
        view !== "day" &&
        draggedTask &&
        pointerPosition &&
        createPortal(
          <div
            className="pointer-events-none fixed z-1300"
            style={{
              left: pointerPosition.x,
              top: pointerPosition.y,
              transform: "translate(-50%, -65%)",
            }}
          >
            <div className="relative">
              <div className="rounded-2xl border border-blue-200 bg-white/95 shadow-2xl backdrop-blur-sm px-3 py-2 min-w-44 max-w-[16rem]">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-blue-500 mb-1 flex items-center gap-1">
                  <span className="inline-flex h-1.5 w-1.5 rounded-full bg-blue-500"></span>
                  Dragging task
                </div>
                <div className="text-sm font-medium text-[#2B2B2B] truncate">
                  {draggedTask.title}
                </div>
                <div className="text-[11px] text-[#616161] truncate mt-1">
                  {draggedTask.assignee} • {draggedTask.taskType}
                </div>
              </div>
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 h-2 w-6 rounded-b-full bg-blue-500/60 blur-sm opacity-70"></div>
            </div>
          </div>,
          document.body
        )}

      {/* View More Tasks Modal */}
      <Dialog.Root
        open={viewMoreModal.isOpen}
        onOpenChange={(open) =>
          setViewMoreModal((prev) => ({ ...prev, isOpen: open }))
        }
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-gray-800/50 z-1100" />
          <Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-xl w-[35vw] max-w-2xl max-h-[80vh] z-1101 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <Dialog.Title className="text-xl font-semibold text-[#444444]">
                Tasks for{" "}
                {viewMoreModal.date &&
                  format(viewMoreModal.date, "MMMM d, yyyy")}
              </Dialog.Title>
              <Dialog.Close asChild>
                <button
                  className="cursor-pointer text-[#828282] hover:text-[#444444] p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                  aria-label="Close"
                >
                  <TbX className="w-5 h-5" />
                </button>
              </Dialog.Close>
            </div>

            {/* Task List */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-3">
                {viewMoreModal.tasks.map((task) => (
                  <ItemCard
                    key={task.id}
                    task={task}
                    onClick={() => {
                      // Find the original task to pass the correct type
                      const originalTask = tasks.find((t) => t.id === task.id);
                      if (originalTask) {
                        onTaskClick?.(originalTask);
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </DndContext>
  );
};

// Droppable Day Component
function DroppableDay({
  day,
  items,
  isToday,
  faded,
  onSelect,
  onTaskClick,
  onViewMore,
}: {
  day: Date;
  items: CalendarTask[];
  isToday: boolean;
  faded: boolean;
  onSelect: (day: Date) => void;
  onTaskClick?: (task: CalendarTask) => void;
  onViewMore?: (date: Date, tasks: CalendarTask[]) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `day-${format(day, "yyyy-MM-dd")}`,
  });

  return (
    <div
      ref={setNodeRef}
      onClick={() => onSelect(day)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(day);
        }
      }}
      className={`h-36 p-2 text-left border-b border-gray-200 relative group hover:bg-gray-50 transition cursor-pointer ${
        faded ? "bg-gray-50" : "bg-white"
      } ${isOver ? "bg-blue-50 border-blue-200" : ""}`}
      role="button"
      tabIndex={0}
    >
      <div className="flex items-center justify-between mb-1">
        <div
          className={`text-xs font-semibold ${
            isToday
              ? "bg-[#374795] text-white rounded-md px-2 py-0.5"
              : "text-[#444444]"
          }`}
        >
          {format(day, "d")}
        </div>
        {items.length > 0 && (
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-[#828282]">
            {items.length} task{items.length > 1 ? "s" : ""}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1 overflow-hidden">
        {items.slice(0, 3).map((t) => (
          <ItemPill key={t.id} task={t} compact onClick={onTaskClick} />
        ))}
        {items.length > 3 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewMore?.(day, items);
            }}
            className="cursor-pointer text-[10px] text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-1 py-0.5 rounded text-left transition-colors duration-200"
          >
            +{items.length - 3} more tasks
          </button>
        )}
      </div>
    </div>
  );
}

function ViewToggle({
  view,
  onChange,
}: {
  view: "day" | "week" | "month";
  onChange: (view: "day" | "week" | "month") => void;
}) {
  const btn = (id: "day" | "week" | "month", label: string) => (
    <button
      key={id}
      onClick={() => onChange(id)}
      className={`cursor-pointer px-3 py-2 rounded-xl border text-sm font-medium transition ${
        view === id
          ? "bg-[#374795] text-white border-[#374795]"
          : "border-gray-200 text-[#444444] hover:bg-gray-50"
      }`}
    >
      {label}
    </button>
  );
  return (
    <div className="inline-flex gap-2">
      {btn("day", "Day")}
      {btn("week", "Week")}
      {btn("month", "Month")}
    </div>
  );
}

function MonthGrid({
  days,
  cursor,
  onSelect,
  tasksOn,
  onTaskClick,
  onViewMore,
}: {
  days: Date[];
  cursor: Date;
  onSelect: (day: Date) => void;
  tasksOn: (day: Date) => CalendarTask[];
  onTaskClick?: (task: CalendarTask) => void;
  onViewMore?: (date: Date, tasks: CalendarTask[]) => void;
}) {
  const weekdayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return (
    <div>
      {/* Weekday header */}
      <div className="grid grid-cols-7 bg-gray-100 text-xs font-semibold border-b border-gray-200">
        {weekdayLabels.map((d) => (
          <div key={d} className="px-3 py-2 text-center text-[#828282]">
            {d}
          </div>
        ))}
      </div>
      {/* Grid */}
      <div className="grid grid-cols-7 divide-x divide-gray-200">
        {days.map((day, i) => {
          const items = tasksOn(day);
          const isToday = isSameDay(day, startOfToday());
          const faded = !isSameMonth(day, cursor);

          return (
            <DroppableDay
              key={i}
              day={day}
              items={items}
              isToday={isToday}
              faded={faded}
              onSelect={onSelect}
              onTaskClick={onTaskClick}
              onViewMore={onViewMore}
            />
          );
        })}
      </div>
    </div>
  );
}

function WeekView({
  start,
  end,
  tasks,
  onSelectDay,
  onTaskClick,
}: {
  start: Date;
  end: Date;
  tasks: CalendarTask[];
  onSelectDay: (day: Date) => void;
  onTaskClick?: (task: CalendarTask) => void;
}) {
  const days = eachDayOfInterval({ start, end });
  const byDay = (d: Date) => tasks.filter((t) => isSameDay(t.dueAt, d));
  const weekdayLong = (d: Date) => format(d, "EEE, MMM d");
  return (
    <div className="grid grid-cols-1 md:grid-cols-7 divide-y md:divide-y-0 md:divide-x divide-gray-200 max-h-[60vh] min-h-56">
      {days.map((d, i) => (
        <DroppableWeekDay
          key={i}
          day={d}
          tasks={byDay(d)}
          onSelectDay={onSelectDay}
          onTaskClick={onTaskClick}
          weekdayLabel={weekdayLong(d)}
        />
      ))}
    </div>
  );
}

// Droppable Week Day Component
function DroppableWeekDay({
  day,
  tasks,
  onSelectDay,
  onTaskClick,
  weekdayLabel,
}: {
  day: Date;
  tasks: CalendarTask[];
  onSelectDay: (day: Date) => void;
  onTaskClick?: (task: CalendarTask) => void;
  weekdayLabel: string;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `day-${format(day, "yyyy-MM-dd")}`,
  });

  return (
    <div
      ref={setNodeRef}
      onClick={() => onSelectDay(day)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelectDay(day);
        }
      }}
      className={`p-3 flex flex-col text-left hover:bg-gray-50 transition-colors cursor-pointer ${
        isOver ? "bg-blue-50 border-blue-200" : ""
      }`}
      role="button"
      tabIndex={0}
    >
      <div className="text-xs font-semibold text-[#444444] hover:underline shrink-0">
        {weekdayLabel}
      </div>
      <div className="mt-2 flex flex-col gap-2 overflow-auto flex-1">
        {tasks.length === 0 && (
          <div className="text-xs text-[#828282]">No tasks</div>
        )}
        {tasks.map((t) => (
          <ItemCard key={t.id} task={t} onClick={() => onTaskClick?.(t)} />
        ))}
      </div>
    </div>
  );
}

function DayView({
  day,
  tasks,
  onTaskClick,
}: {
  day: Date;
  tasks: CalendarTask[];
  onTaskClick?: (task: CalendarTask) => void;
}) {
  return (
    <div className="p-4 max-h-[66vh] overflow-auto">
      <div className="relative text-sm font-semibold text-[#444444] top-0 bg-white pb-2">
        {format(day, "EEEE, MMMM d, yyyy")}
      </div>
      <div className="mt-3 flex flex-col gap-2">
        {tasks.length === 0 && (
          <div className="text-sm text-[#828282]">No tasks due</div>
        )}
        {tasks.map((t) => (
          <ItemCard key={t.id} task={t} onClick={() => onTaskClick?.(t)} />
        ))}
      </div>
    </div>
  );
}

function ItemPill({
  task,
  compact,
  onClick,
}: {
  task: CalendarTask;
  compact?: boolean;
  onClick?: (task: CalendarTask) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: task.id,
    });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  const handleClick = (e: React.MouseEvent) => {
    // Prevent click when dragging
    if (isDragging) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    // Stop propagation so clicking a task doesn't also trigger the day cell click
    e.stopPropagation();
    onClick?.(task);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-lg border border-gray-200 px-2 py-1 ${
        compact ? "text-[10px]" : "text-xs"
      } bg-white shadow-sm truncate cursor-pointer hover:bg-gray-50 touch-none transition-all duration-200 ${
        isDragging ? "opacity-50 z-50 pointer-events-none" : ""
      }`}
      title={`${task.title} — ${task.assignee} • ${task.taskType} • ${task.assetType}`}
      onClick={handleClick}
      {...listeners}
      {...attributes}
    >
      <div className="flex items-center gap-1 truncate">
        <span className="font-medium truncate text-[#444444]">
          {task.title}
        </span>
      </div>
      {!compact && (
        <div className="text-[10px] text-[#828282] truncate">
          {task.assignee} • {task.taskType} • {task.assetType}
        </div>
      )}
    </div>
  );
}

function ItemCard({
  task,
  onClick,
}: {
  task: CalendarTask;
  onClick?: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: task.id,
    });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  const handleClick = (e: React.MouseEvent) => {
    // Prevent click when dragging
    if (isDragging) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    // Stop propagation so clicking a task doesn't also trigger the day cell click
    e.stopPropagation();
    onClick?.();
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-2xl border border-gray-200 p-3 shadow-sm hover:shadow hover:bg-gray-50 transition-all duration-200 bg-white cursor-pointer touch-none ${
        isDragging ? "opacity-50 z-50 pointer-events-none" : ""
      }`}
      onClick={handleClick}
      {...listeners}
      {...attributes}
    >
      <div className="text-sm font-semibold leading-tight text-[#444444]">
        {task.title}
      </div>
      <div className="text-xs text-[#828282] mt-0.5">
        Due {format(task.dueAt, "EEE, MMM d")}
        {task.location ? ` • ${task.location}` : ""}
      </div>
      <div className="flex flex-wrap gap-1 mt-2">
        <Badge>{task.assignee}</Badge>
        <Badge>{task.taskType}</Badge>
        <Badge>{task.assetType}</Badge>
        {task.status && <Badge variant="subtle">{task.status}</Badge>}
      </div>
    </div>
  );
}

function Badge({
  children,
  variant,
}: {
  children: React.ReactNode;
  variant?: string;
}) {
  const subtle = variant === "subtle";
  return (
    <span
      className={`text-[10px] px-2 py-1 rounded-full border border-gray-200 ${
        subtle ? "bg-gray-50 text-[#828282]" : "bg-white text-[#444444]"
      }`}
    >
      {children}
    </span>
  );
}

function MultiSelect({
  label,
  options,
  values,
  setValues,
}: {
  label: string;
  options: string[];
  values: string[];
  setValues: (values: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggle = (v: string) => {
    if (values.includes(v)) setValues(values.filter((x) => x !== v));
    else setValues([...values, v]);
  };
  const clear = () => setValues([]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter((opt) =>
      opt.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  // Clear search when dropdown closes
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSearchTerm("");
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        handleOpenChange(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [open]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => handleOpenChange(!open)}
        className="cursor-pointer w-full flex items-center justify-between gap-2 border border-gray-200 rounded-xl px-3 py-1 bg-gray-100"
      >
        <div className="text-left">
          <div className="text-[11px] uppercase tracking-wide text-[#828282]">
            {label}
          </div>
          <div className="text-sm truncate text-[#444444]">
            {values.length ? values.join(", ") : "All"}
          </div>
        </div>
        <TbFilter className="w-4 h-4 text-[#828282]" />
      </button>
      {open && (
        <div className="absolute z-20 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-xl p-2 max-h-64 overflow-hidden">
          <div className="flex items-center justify-between px-2 py-1 text-[11px] uppercase tracking-wide text-[#828282]">
            <span>Select {label.toLowerCase()}</span>
            {values.length > 0 && (
              <button
                onClick={clear}
                className="cursor-pointer text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-gray-200"
              >
                <TbX className="w-3 h-3" />
                Clear
              </button>
            )}
          </div>

          {/* Search Input */}
          <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-2 py-1 mb-2 bg-gray-50">
            <TbSearch className="w-3 h-3 text-[#828282]" />
            <input
              type="text"
              placeholder={`Search ${label.toLowerCase()}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-transparent outline-none text-xs text-[#444444] placeholder:text-[#828282]"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div className="max-h-40 overflow-auto">
            <ul className="grid gap-1">
              {filteredOptions.length === 0 && (
                <li className="text-xs text-[#828282] px-2 py-2">
                  {searchTerm
                    ? `No ${label.toLowerCase()} found`
                    : "No options"}
                </li>
              )}
              {filteredOptions.map((opt) => (
                <li key={opt}>
                  <label className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-gray-100 cursor-pointer">
                    <input
                      type="checkbox"
                      className="accent-[#374795]"
                      checked={values.includes(opt)}
                      onChange={() => toggle(opt)}
                    />
                    <span className="text-sm text-[#444444]">{opt}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
